/*      Dara - Organisation Student
 * All axios endpoints for organisation students. They will be prefixed with /api/organisations/{organisation}/students/
 */
import axios from '@/plugins/axios'
import type {
  ICreate,
  IGetColumnMappingData,
  IGetStudentPreImportData,
  IImportStudents,
} from '@/interfaces/api/Dara/OrganisationStudent'
import type { IStudentFilter } from '@/interfaces/api/Dara/StudentFilter'
import type IDataResponse from '@/interfaces/api-resources/DataResponse'
import type { IStudentSendDetailsResponse } from '@/interfaces/api-resources/Dara/Student'
import type IGroup from '@/models/Group/Group'
import type IStudentUploadMappingField from '@/models/StudentUpload/MappingField'
import type IStudentUploadStudent from '@/models/StudentUpload/StudentUploadStudent'
import { useAppStore } from '@/stores/app'
import type IPaginatedAdditionalResponse from '@/interfaces/api-resources/PaginatedAdditionalResponse'
import type { IStudentOverview, IStudentOverviewAdditional } from '@/interfaces/api-resources/Dara/StudentOverview'
import type IStudent from '@/models/Student/Student'

interface IStoreData {
  first_name: string
  last_name: string
  known_as: string
  birth: string | null,
  age: string
  email_address: string
  has_sen: string | boolean
  sen_reason_id: string
  saf_score: string
  year_group_id: string
  year_sub_group_id: string
}

export default {
  baseUrl(organisationId: number) {
    const appStore = useAppStore()

    return `${appStore.daraApiBaseUrl}/organisations/${organisationId}/students/`
  },

  create(organisationId: number)  {
    return axios.get<ICreate, ICreate>(this.baseUrl(organisationId) + 'create')
  },

  getColumnMappingData(organisationId: number, data: FormData) {
    return axios.post<IGetColumnMappingData, IGetColumnMappingData>(this.baseUrl(organisationId) + `column-mapping`, data)
  },

  getStudentsPreImportData(organisationId: number, data: { mappings: IStudentUploadMappingField[], spreadsheet: File|null }) {
    return axios.post<IGetStudentPreImportData, IGetStudentPreImportData>(this.baseUrl(organisationId) + `pre-import`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  importStudents(organisationId: number, data: { groups: IGroup[], students: IStudentUploadStudent[], update_existing_students: boolean }) {
    return axios.post<IImportStudents, IImportStudents>(this.baseUrl(organisationId) + `import`, data)
  },

  store(organisationId: number, data: IStoreData) {
    return axios.post<IDataResponse<IStudent>, IDataResponse<IStudent>>(this.baseUrl(organisationId), data)
  },

  get(organisationId: number, filters: IStudentFilter, locale?: string | null) {
    // filter out empty strings and optional booleans
    const cleanedFilters = Object.fromEntries(Object.entries(filters)
      .filter(([key, value]) => key !== 'only_has_left' || value === true)
      .filter(([key, value]) => key !== 'only_has_sen' || value === true)
      .filter(([key, value]) => key !== 'include_trial' || value === true)
      .filter(([, value]) => value !== ''),
    )

    if (locale) {
      cleanedFilters.locale = locale
    }

    return axios.get<
      IPaginatedAdditionalResponse<IStudentOverview[], IStudentOverviewAdditional>,
      IPaginatedAdditionalResponse<IStudentOverview[], IStudentOverviewAdditional>
    >(this.baseUrl(organisationId), { params: cleanedFilters })
  },

  search(organisationId: number, search: string) {
    const params = { search: search }

    return axios.get<
      IDataResponse<Domain.Students.Resources.ApiV3.StudentNameResource[]>,
      IDataResponse<Domain.Students.Resources.ApiV3.StudentNameResource[]>
    >(this.baseUrl(organisationId) + 'search', { params: params })
  },

  sendUserDetails(organisationId: number, data: { students: number[] }) {
    return axios.post<IDataResponse<IStudentSendDetailsResponse>, IDataResponse<IStudentSendDetailsResponse>>(this.baseUrl(organisationId) + 'send-user-details', data)
  },

  setGroups(organisationId: number, data: object) {
    return axios.post(this.baseUrl(organisationId) + 'groups', data)
  },

  updateStudentsAccess(organisationId: number, data: object)  {
    return axios.post(this.baseUrl(organisationId) + 'access', data)
  },

  updateStudentsAccessCreate(organisationId: number, data: object)  {
    return axios.post(this.baseUrl(organisationId) + 'access-create', data)
  },
}
